import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Trophy, FlaskConical, ThumbsDown, ThumbsUp, FileSpreadsheet, Search, CheckCircle2, XCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";

const NAVY = "#0B1F3A", BLUE = "#1D4ED8", EMERALD = "#059669";
const PIE_COLORS = [BLUE, EMERALD, "#5B8CFF", "#34D399", "#93B4FF", "#6EE7B7", "#1638A8"];
const LEAD_STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const LEAD_TYPE_LABELS = { consult: "Consult request", verified_roadmap_request: "Verified roadmap request", analyzer_interest: "Analyzer waitlist" };
const USER_CRM_STATUSES = ["new", "contacted", "qualified", "customer"];

export function ExportXlsxButton({ kind, label }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RegNav ${kind} ${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed — try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={download} disabled={busy}
      className="inline-flex items-center gap-1.5 bg-[#059669] text-white rounded-md px-3.5 py-2 text-xs font-semibold hover:bg-[#059669]/90 disabled:opacity-50"
      data-testid={`export-${kind}-btn`}>
      <FileSpreadsheet size={13} /> {busy ? "Preparing…" : label}
    </button>
  );
}

function VerifiedBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${ok ? "text-[#059669] bg-emerald-50" : "text-[#475569] bg-[#0B1F3A]/5"}`}>
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {label}
    </span>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  }, []);

  const update = async (id, patch) => {
    try {
      const { data } = await api.put(`/admin/users/${id}/crm`, patch);
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, ...data } : u)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (users === null) return <div className="h-40 bg-[#0B1F3A]/10 rounded-xl animate-pulse mt-4" />;

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? users.filter((u) => [u.name, u.email, u.company, u.phone, u.role_title].some((v) => (v || "").toLowerCase().includes(needle)))
    : users;

  return (
    <div className="mt-4" data-testid="admin-users">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…"
            className="pl-8 pr-3 py-2 text-xs border border-[#0B1F3A]/20 rounded-md bg-white w-64 focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="users-search-input" />
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs text-[#475569]">{shown.length} of {users.length} users</p>
          <ExportXlsxButton kind="users" label="Export to Excel" />
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-[#475569] py-10 text-center">No users match — real signups appear here with their profile and verification status.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#0B1F3A]/10 rounded-xl mt-3">
          <table className="w-full text-xs min-w-[980px]" data-testid="admin-users-table">
            <thead>
              <tr className="bg-[#0B1F3A] text-white text-left">
                {["Signed up", "Name", "Email", "Phone", "Company / role", "Verification", "Projects", "Roadmaps", "CRM status", "Notes"].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.id} className="border-t border-[#0B1F3A]/10 align-top" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2.5 whitespace-nowrap text-[#475569]">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2.5 font-medium text-[#0B1F3A]">{u.name || "—"}</td>
                  <td className="px-3 py-2.5">{u.email}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{u.phone || "—"}</td>
                  <td className="px-3 py-2.5">{u.company || "—"}{u.role_title ? <span className="text-[#475569]"> · {u.role_title}</span> : ""}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap space-x-1">
                    <VerifiedBadge ok={!!u.email_verified} label="Email" />
                    <VerifiedBadge ok={!!u.phone_verified} label="Mobile" />
                  </td>
                  <td className="px-3 py-2.5 text-center">{u.projects_count}</td>
                  <td className="px-3 py-2.5 text-center">{u.roadmaps_count}</td>
                  <td className="px-3 py-2.5">
                    <select value={u.crm_status || "new"} onChange={(e) => update(u.id, { crm_status: e.target.value })}
                      className="text-xs border border-[#0B1F3A]/20 rounded-md px-2 py-1 bg-white capitalize" data-testid={`user-crm-status-${u.id}`}>
                      {USER_CRM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5 min-w-[180px]">
                    <input defaultValue={u.crm_notes || ""} placeholder="Notes (saved on blur)"
                      onBlur={(e) => e.target.value !== (u.crm_notes || "") && update(u.id, { crm_notes: e.target.value })}
                      className="w-full text-xs border border-[#0B1F3A]/15 rounded-md px-2 py-1" data-testid={`user-crm-notes-${u.id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminLeads() {
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => setLeads([]));
  }, []);

  const update = async (id, patch) => {
    try {
      const { data } = await api.put(`/admin/leads/${id}`, patch);
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (leads === null) return <div className="h-40 bg-[#0B1F3A]/10 rounded-xl animate-pulse mt-4" />;
  if (leads.length === 0) return <p className="text-sm text-[#475569] py-10 text-center">No leads yet — they'll appear here the moment someone requests a consult or a verified roadmap.</p>;

  return (
    <div className="space-y-3 mt-4" data-testid="admin-leads-list">
      <div className="flex justify-end">
        <ExportXlsxButton kind="leads" label="Export to Excel" />
      </div>
      {leads.map((l) => (
        <div key={l.id} className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4" data-testid={`lead-row-${l.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#1D4ED8] bg-blue-50 rounded-full px-2 py-0.5">{LEAD_TYPE_LABELS[l.type] || l.type}</span>
                <span className="text-xs text-[#475569]">{new Date(l.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <p className="font-medium text-[#0B1F3A] text-sm mt-1.5">{l.name || "—"} · {l.email}{l.phone ? ` · ${l.phone}` : ""}{l.company ? ` · ${l.company}` : ""}</p>
              {l.note && <p className="text-xs text-[#475569] mt-1">{l.note}</p>}
              {l.context?.answers && (
                <p className="text-[11px] text-[#059669] mt-1">
                  Project: {l.context.answers.project_type?.replace(/_/g, " ")} · {l.context.answers.capacity_mw ? `${l.context.answers.capacity_mw} MW · ` : ""}{l.context.answers.state} · {l.context.answers.connectivity?.toUpperCase()}
                </p>
              )}
            </div>
            <select value={l.status} onChange={(e) => update(l.id, { status: e.target.value })}
              className="text-xs border border-[#0B1F3A]/20 rounded-md px-2 py-1.5 bg-white font-medium capitalize" data-testid={`lead-status-${l.id}`}>
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input defaultValue={l.admin_notes || ""} placeholder="Notes (saved on blur)" onBlur={(e) => e.target.value !== (l.admin_notes || "") && update(l.id, { admin_notes: e.target.value })}
            className="mt-3 w-full text-xs border border-[#0B1F3A]/15 rounded-md px-3 py-2" data-testid={`lead-notes-${l.id}`} />
        </div>
      ))}
    </div>
  );
}

export function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="h-64 bg-[#0B1F3A]/10 rounded-xl animate-pulse mt-4" />;
  const t = data.totals;

  return (
    <div className="space-y-5 mt-4" data-testid="admin-analytics">
      <div className="band-navy text-white rounded-2xl p-6" data-testid="contest-metrics-card">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#34D399] flex items-center gap-2"><Trophy size={14} /> Contest metrics</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-4">
          {[["Roadmaps generated", data.contest.roadmaps_generated], ["Registered users", data.contest.registered_users],
            ["Companies represented", data.contest.companies_represented], ["Consult leads", data.contest.consult_leads],
            ["PDFs downloaded", data.contest.pdfs_downloaded]].map(([l, v]) => (
            <div key={l}>
              <p className="font-serif text-3xl font-semibold text-white">{v}</p>
              <p className="text-[11px] text-white/60 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Wizard runs", t.roadmaps_generated], ["Signups", t.signups], ["Projects saved", t.projects_saved], ["PDF downloads", t.pdf_downloads],
          ["Share-link views", t.share_views], ["Demo sessions", t.demo_sessions], ["Chat messages", t.chat_messages],
          ["Consults + verified requests", t.consult_requests + t.verified_roadmap_requests]].map(([l, v]) => (
          <div key={l} className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4">
            <p className="font-serif text-2xl font-semibold text-[#1D4ED8]">{v}</p>
            <p className="text-[11px] text-[#475569] mt-1">{l}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-3">Roadmaps generated by day</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.runs_by_day}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke={BLUE} strokeWidth={2.5} dot={{ r: 3, fill: EMERALD }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-3">Runs by project type</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.runs_by_type} dataKey="count" nameKey="type" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {data.runs_by_type.map((d, i) => <Cell key={d.type || `type-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-1">
            {data.runs_by_type.map((d, i) => (
              <span key={d.type || `type-${i}`} className="text-[10px] text-[#475569] flex items-center gap-1">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{d.type} ({d.count})
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4 lg:col-span-2">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-3">Runs by state</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.runs_by_state}>
              <XAxis dataKey="state" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip />
              <Bar dataKey="count" fill={EMERALD} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4" data-testid="feedback-panel">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-2">Roadmap feedback</p>
          <div className="flex gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-[#059669] font-semibold"><ThumbsUp size={14} /> {t.feedback_up}</span>
            <span className="inline-flex items-center gap-1.5 text-red-500 font-semibold"><ThumbsDown size={14} /> {t.feedback_down}</span>
          </div>
          <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
            {data.feedback_comments.length === 0 && <p className="text-xs text-[#475569]">No comments yet.</p>}
            {data.feedback_comments.map((f) => (
              <p key={f.id} className="text-xs text-[#0B1F3A] border-l-2 pl-2 py-0.5" style={{ borderColor: f.rating === "down" ? "#EF4444" : EMERALD }}>
                {f.comment} <span className="text-[#475569]">· {new Date(f.created_at).toLocaleDateString("en-IN")}</span>
              </p>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-2">Chat questions beyond the KB (knowledge-base backlog)</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {data.beyond_kb_questions.length === 0 && <p className="text-xs text-[#475569]">Nothing yet — the KB is covering the questions asked.</p>}
            {data.beyond_kb_questions.map((q, i) => (
              <p key={q.created_at || `q-${i}`} className="text-xs text-[#0B1F3A] border-l-2 border-violet-400 pl-2 py-0.5">{q.content}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminResearch({ onDraftsCreated }) {
  const [form, setForm] = useState({ scope: "", state: "", project_type: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => api.get("/admin/ai-research").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/ai-research", form);
      load();
      // job runs in the background (~2 min LLM call) — poll history until it finishes
      const started = Date.now();
      const poll = async () => {
        const { data: hist } = await api.get("/admin/ai-research");
        setHistory(hist);
        const rec = hist.find((h) => h.id === data.id);
        if (rec && rec.status === "done") {
          setResult(rec);
          setBusy(false);
          onDraftsCreated?.();
          toast.success(`AI drafted ${rec.approvals.length} approvals + ${rec.rules.length} rules — pending your review`);
          return;
        }
        if (rec && rec.status === "failed") {
          setBusy(false);
          toast.error("AI research failed — try again, or add the entries manually in the approval library");
          return;
        }
        if (Date.now() - started > 5 * 60 * 1000) {
          setBusy(false);
          toast.error("Research is taking unusually long — check the history panel in a minute");
          return;
        }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 5000);
    } catch (err) {
      setBusy(false);
      toast.error(apiError(err));
    }
  };

  return (
    <div className="mt-4 grid lg:grid-cols-2 gap-5" data-testid="admin-research">
      <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-5">
        <p className="font-serif font-semibold text-[#0B1F3A] flex items-center gap-2"><FlaskConical size={16} className="text-[#1D4ED8]" /> Draft new coverage with AI</p>
        <p className="text-xs text-[#475569] mt-1">Drafts land as "AI Draft — Pending Expert Review" in the approval library. Nothing reaches users until you approve it.</p>
        <form onSubmit={run} className="space-y-3 mt-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Scope to research</label>
            <input required value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
              placeholder="e.g., Open access approvals — industrial consumer 5 MW solar"
              className="mt-1 w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="research-scope-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">State</label>
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="e.g., Chhattisgarh"
                className="mt-1 w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="research-state-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Project type</label>
              <input value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} placeholder="e.g., solar_ground"
                className="mt-1 w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="research-type-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Research notes / pasted source material (optional)</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4}
              placeholder="Paste regulation extracts, tariff-order text, or your own notes — the AI structures them"
              className="mt-1 w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="research-notes-input" />
          </div>
          <button type="submit" disabled={busy} className="w-full bg-[#1D4ED8] text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="research-run-btn">
            {busy ? "Researching in the background — usually 1–3 minutes…" : "Draft approvals & rules with AI"}
          </button>
        </form>
        {result && (
          <div className="mt-4 bg-emerald-50 border border-[#059669]/40 rounded-lg p-4 fade-up" data-testid="research-result">
            <p className="text-sm font-semibold text-[#059669]">Drafted — pending your expert review</p>
            <p className="text-xs text-[#0B1F3A] mt-2"><b>Approvals:</b> {result.approvals.map((a) => a.name).join(" · ") || "none"}</p>
            <p className="text-xs text-[#0B1F3A] mt-1"><b>Rules:</b> {result.rules.map((r) => r.name).join(" · ") || "none"}</p>
            {result.assumptions && <p className="text-xs text-[#475569] mt-2"><b>Assumptions & sources to verify:</b> {result.assumptions}</p>}
            <p className="text-[11px] text-[#475569] mt-2">Review and approve them in the Approval library tab.</p>
          </div>
        )}
      </div>
      <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-5">
        <p className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Research history</p>
        <div className="mt-3 space-y-3 max-h-[480px] overflow-y-auto">
          {history.length === 0 && <p className="text-xs text-[#475569]">No research runs yet.</p>}
          {history.map((h) => (
            <div key={h.id} className="border border-[#0B1F3A]/10 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-[#0B1F3A]">{h.scope}{h.state ? ` — ${h.state}` : ""}</p>
                {h.status === "processing" && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 animate-pulse">Researching…</span>}
                {h.status === "failed" && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Failed</span>}
              </div>
              <p className="text-[11px] text-[#475569] mt-1">{h.approvals.length} approvals · {h.rules.length} rules · {new Date(h.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
